import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { logEvent } from 'firebase/analytics';
import { BOARD_LAYOUT } from '../../boardLayout';
import { analyticsInstanceSelector } from '../../services/GameSlice';
import { BoardFace, HandFace } from '../Board/CardFace';
import ShareButtons from '../Share/ShareButtons';
import copy from 'copy-to-clipboard';
import { setBody, setType } from '../../services/NotificationSlice';
import { emitCommand, getSocket } from '../../socket';
import { getPlayerKey, getUserId } from '../../identity';
import {
    KINDS, MAX_TRIES, SITE, cardName, isOneEyedJack, lineThrough, msToNext, puzzleNumber, shareText,
} from './puzzleRules';
import './daily.css';

// One "find the move" puzzle a day, the same for everyone. The game server
// sends today's board and hand only, checks every try and keeps the
// leaderboard (sequence-be/services/dailyPuzzle.js); it never touches a game.

const CHIP = { blue: '#2F6FDB', green: '#22A36E' };

const ROWS = Array.from({ length: 10 }, (_, i) => i);

const countdown = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

const Board = ({ puzzle, selected, misses, reveal, onPick }) => {
    const chips = useMemo(() => Object.fromEntries(puzzle.moves.map(m => [m.pos, m.team])), [puzzle]);
    const removing = selected && isOneEyedJack(selected);
    const playable = (pos) => {
        if (!selected) return false;
        if (removing) return chips[pos] === 'green';
        return !chips[pos] && BOARD_LAYOUT[pos] === selected;
    };
    const answer = reveal?.pos;
    const threats = new Set(reveal?.threats || []);
    // The five the answer makes (Win in 1) or stops (Stop them).
    const line = new Set(answer && puzzle.kind !== 'fork'
        ? lineThrough(puzzle.moves, answer, puzzle.kind === 'block' ? 'green' : 'blue')
        : []);
    return (
        <div className={`dp-board${selected ? ' aiming' : ''}`} role="grid" aria-label="Puzzle board">
            {ROWS.map(i => ROWS.map(j => {
                const pos = `${i},${j}`;
                const card = BOARD_LAYOUT[pos];
                const team = chips[pos];
                const can = playable(pos);
                const missed = misses.includes(pos);
                return (
                    <button
                        type="button"
                        key={pos}
                        className={`dp-cell${can ? ' playable' : ''}${missed ? ' missed' : ''}${answer === pos ? ' answer' : ''}${threats.has(pos) ? ' threat' : ''}${line.has(pos) && answer !== pos ? ` line line-${puzzle.kind}` : ''}`}
                        disabled={!can}
                        onClick={() => onPick(pos)}
                        aria-label={`${cardName(card)}${team ? `, ${team} chip` : ''}${can ? ', playable' : ''}`}
                    >
                        <BoardFace card={card}/>
                        {team && <span className="chip" style={{ '--c': CHIP[team] }} aria-hidden="true"/>}
                        {answer === pos && <span className="chip dp-answer-chip" style={{ '--c': CHIP.blue }} aria-hidden="true"/>}
                        {missed && <span className="dp-miss-mark" aria-hidden="true">✕</span>}
                    </button>
                );
            }))}
        </div>
    );
};

const clock = (seconds) => (seconds >= 60 ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : `${seconds}s`);

/** Today's leaderboard: who solved it in fewest tries and least time, and live streaks. */
const Standings = ({ board }) => {
    if (!board) return null;
    const total = board.players || 0;
    const bars = [['1', board.spread[1]], ['2', board.spread[2]], ['3', board.spread[3]], ['X', board.spread.failed]];
    const most = Math.max(1, ...bars.map(([, n]) => n));
    const showYou = board.you && !board.fastest.some(row => row.isYou);
    return (
        <div className="dp-board-panel">
            <h2>Today’s leaderboard</h2>
            <p className="dp-board-summary">
                {total === 0 ? 'Nobody has finished today’s puzzle yet. Be the first.' : `${total} ${total === 1 ? 'player has' : 'players have'} played, ${board.solved} solved it.`}
            </p>
            {total > 0 && <div className="dp-spread" aria-label="How many tries players needed">
                {bars.map(([label, n]) => (
                    <div key={label} className="dp-spread-row">
                        <span className="dp-spread-label">{label}</span>
                        <span className="dp-spread-bar"><span className={label === 'X' ? 'failed' : ''} style={{ width: `${(n / most) * 100}%` }}/></span>
                        <span className="dp-spread-count">{n}</span>
                    </div>
                ))}
            </div>}
            {board.fastest.length > 0 && <ol className="dp-ranks">
                {board.fastest.slice(0, 10).map(row => (
                    <li key={row.rank} className={row.isYou ? 'you' : ''}>
                        <span className="dp-rank">{row.rank}</span>
                        <span className="dp-name">{row.name}{row.isYou ? ' (you)' : ''}</span>
                        <span className="dp-score">{'🟩'.padStart(row.tries * 2, '🟥')} {clock(row.seconds)}</span>
                    </li>
                ))}
                {showYou && <li className="you dp-you-gap">
                    <span className="dp-rank">{board.you.rank}</span>
                    <span className="dp-name">{board.you.name} (you)</span>
                    <span className="dp-score">{'🟩'.padStart(board.you.tries * 2, '🟥')} {clock(board.you.seconds)}</span>
                </li>}
            </ol>}
            {board.streaks.length > 0 && <>
                <h3>Longest streaks</h3>
                <ol className="dp-ranks">
                    {board.streaks.map(row => (
                        <li key={row.rank} className={row.isYou ? 'you' : ''}>
                            <span className="dp-rank">{row.rank}</span>
                            <span className="dp-name">{row.name}{row.isYou ? ' (you)' : ''}</span>
                            <span className="dp-score">🔥 {row.streak} days</span>
                        </li>
                    ))}
                </ol>
            </>}
            <p className="dp-board-note">Ranked by fewest tries, then fastest time. Guests show under a made-up name; <Link to="/leaderboard">sign in</Link> to choose your own.</p>
        </div>
    );
};

const DailyPuzzle = () => {
    const analytics = useSelector(analyticsInstanceSelector);
    const dispatch = useDispatch();
    const [puzzle, setPuzzle] = useState(null);
    const [failed, setFailed] = useState('');
    const [board, setBoard] = useState(null);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [now, setNow] = useState(() => new Date());
    const [attempt, setAttempt] = useState(0);
    // Local development only: ?n=12 opens puzzle #12 (with DAILY_ANY_DAY=1 on
    // the local server), to check any of them.
    const devNumber = process.env.NODE_ENV === 'development' ? Number(new URLSearchParams(window.location.search).get('n')) : 0;
    const number = devNumber > 0 ? devNumber : puzzleNumber(now);

    const loadBoard = useCallback(() => {
        emitCommand('daily:board', { number, userId: getUserId() }).then(setBoard).catch(() => { /* the board is extra */ });
    }, [number]);

    useEffect(() => {
        // The pre-rendered copy of this page is made by a headless browser:
        // leave the board out of it, so nobody sees a day-old puzzle before
        // the page loads.
        if (navigator.webdriver) return undefined;
        let gone = false;
        setPuzzle(null);
        setFailed('');
        setSelected(null);
        setMessage('');
        emitCommand('daily:start', { number, userId: getUserId(), key: getPlayerKey() })
            .then(data => {
                if (gone) return;
                setPuzzle(data);
                if (analytics) logEvent(analytics, 'daily_open', { puzzle: number, kind: data.kind });
            })
            .catch(error => { if (!gone) setFailed(error.message); });
        loadBoard();
        return () => { gone = true; };
    }, [number, attempt, loadBoard, analytics]);

    // The board refreshes when anyone finishes today's puzzle.
    useEffect(() => {
        if (navigator.webdriver) return undefined;
        const socket = getSocket();
        const changed = (event) => { if (event?.number === number) loadBoard(); };
        const rejoin = () => socket.emit('daily:watch');
        socket.emit('daily:watch');
        socket.on('daily:changed', changed);
        socket.on('connect', rejoin);
        return () => {
            socket.emit('daily:unwatch');
            socket.off('daily:changed', changed);
            socket.off('connect', rejoin);
        };
    }, [number, loadBoard]);

    // The countdown, and a fresh puzzle when the day turns over with the page open.
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);

    const kind = puzzle ? KINDS[puzzle.kind] : null;
    const done = Boolean(puzzle?.done);
    const solved = done && puzzle.solved;
    const tries = puzzle?.tries || [];

    const pick = (pos) => {
        if (!puzzle || done || !selected || busy) return;
        const card = selected;
        setBusy(true);
        emitCommand('daily:guess', { number, userId: getUserId(), key: getPlayerKey(), card, pos })
            .then(result => {
                setPuzzle(p => ({ ...p, ...result }));
                setSelected(null);
                if (result.done) {
                    setMessage('');
                    loadBoard();
                    if (result.finished && analytics) {
                        logEvent(analytics, result.solved ? 'daily_solve' : 'daily_fail', { puzzle: number, kind: puzzle.kind, tries: result.tries.length });
                    }
                } else {
                    setMessage(isOneEyedJack(card) ? "Taking a chip off doesn't win this one." : kind.miss);
                }
            })
            .catch(error => setMessage(error.message))
            .finally(() => setBusy(false));
    };

    const answer = done ? puzzle.answer : null;
    const text = done ? shareText(number, puzzle.kind, tries, puzzle.streak) : '';

    return (
        <Box>
            <Helmet>
                <title>Daily Sequence Puzzle – Find the Winning Move | Play Sequence</title>
                <meta name="description" content="A new Sequence board game puzzle every day: find the winning move, block the threat or set up a double. Free, three tries, a daily leaderboard and streaks." />
                <meta property="og:title" content="Daily Sequence Puzzle" />
                <meta property="og:description" content="A new Sequence puzzle every day. Find the move in three tries, climb today's leaderboard and keep your streak going." />
            </Helmet>

            <section className="dp-table">
                <div className="dp-inner">
                    <div className="dp-head">
                        <h1>Daily Sequence Puzzle{puzzle ? ` #${number}` : ''}</h1>
                        {kind && <span className={`dp-kind dp-kind-${puzzle.kind}`}>{kind.label}</span>}
                    </div>
                    {kind && <p className="dp-task">{kind.task}</p>}
                    {!puzzle && !failed && <p className="dp-task">Loading today's puzzle…</p>}
                    {failed && <div className="dp-task">
                        <p>Today's puzzle didn't load: {failed}</p>
                        <Button variant="outlined" onClick={() => setAttempt(a => a + 1)} sx={{ color: '#F3EEE2', borderColor: 'rgba(243, 238, 226, 0.35)' }}>Try again</Button>
                    </div>}

                    {puzzle && <>
                        <div className="dp-status" aria-live="polite">
                            <span className="dp-tries">
                                {Array.from({ length: MAX_TRIES }).map((_, i) => (
                                    <span key={i} className={`dp-try ${tries[i] || 'left'}`} aria-hidden="true"/>
                                ))}
                                <span className="dp-tries-label">{done ? (solved ? `Solved in ${tries.length}` : 'Out of tries') : `${MAX_TRIES - tries.length} ${MAX_TRIES - tries.length === 1 ? 'try' : 'tries'} left`}</span>
                            </span>
                            {puzzle.streak > 0 && <span className="dp-streak">🔥 {puzzle.streak}-day streak</span>}
                        </div>

                        <Board
                            puzzle={puzzle}
                            selected={done || busy ? null : selected}
                            misses={done ? [] : puzzle.misses}
                            reveal={answer}
                            onPick={pick}
                        />

                        {!done && <>
                            <p className="dp-hint">{message || (selected ? `Now tap a highlighted space for the ${cardName(selected)}.` : 'Tap a card in your hand, then a space on the board.')}</p>
                            <div className="dp-hand" role="group" aria-label="Your hand">
                                {puzzle.hand.map((card, i) => (
                                    <button
                                        type="button"
                                        key={`${card}-${i}`}
                                        className={`dp-card${selected === card ? ' selected' : ''}`}
                                        onClick={() => {
                                            setSelected(selected === card ? null : card);
                                            setMessage('');
                                        }}
                                        aria-pressed={selected === card}
                                        aria-label={cardName(card)}
                                    >
                                        <HandFace card={card}/>
                                    </button>
                                ))}
                            </div>
                        </>}

                        {done && <div className="dp-result">
                            <h2>{solved ? (tries.length === 1 ? 'First try!' : 'Solved!') : 'Here’s the answer'}</h2>
                            <p>{kind.answer(cardName(answer.card))}{puzzle.otherAnswers > 0 ? ' (Another move works too.)' : ''}</p>
                            {board?.you && <p>You’re <strong>#{board.you.rank}</strong> of {board.solved} on today’s leaderboard.</p>}
                            <pre className="dp-share-text">{text}</pre>
                            <Button
                                variant="contained"
                                sx={{ mb: 1.5, backgroundColor: '#E2B65B', color: '#16201C', '&:hover': { backgroundColor: '#CDA049' } }}
                                onClick={() => {
                                    copy(`${text}\n${SITE}/daily`);
                                    dispatch(setType('success'));
                                    dispatch(setBody('Result copied. Paste it to your friends.'));
                                    if (analytics) logEvent(analytics, 'share', { method: 'copy_result', content_type: 'daily', item_id: String(number) });
                                }}
                            >Copy result</Button>
                            <ShareButtons url={`${SITE}/daily`} text={text} kind="daily" itemId={String(number)} tone="dark"/>
                            <p className="dp-next">Next puzzle in {countdown(msToNext(now))}.</p>
                            <div className="dp-cta">
                                <Button component={Link} to="/" variant="outlined" sx={{ color: '#F3EEE2', borderColor: 'rgba(243, 238, 226, 0.35)' }}>
                                    Play a full game
                                </Button>
                                <Button component={Link} to="/play-against-computer" variant="outlined" sx={{ color: '#F3EEE2', borderColor: 'rgba(243, 238, 226, 0.35)' }}>
                                    Try the Hard computer
                                </Button>
                            </div>
                        </div>}
                    </>}

                    <Standings board={board}/>
                </div>
            </section>

            <Container maxWidth="md" sx={{ py: 6 }}>
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, color: '#16201C' }}>How the daily puzzle works</Typography>
                    <Typography variant="body1" sx={{ color: '#3E4A45', lineHeight: 1.8, mb: 2 }}>
                        Every day there is one new Sequence position, the same for everyone. You play blue. Pick a card from your hand and a space on the board, and you have three tries to find the move. A new puzzle starts at midnight, your time.
                    </Typography>
                    <Box component="ul" sx={{ color: '#3E4A45', lineHeight: 1.8, pl: 3, mt: 0, mb: 2, '& li': { mb: 1 } }}>
                        <li><strong>Win in 1:</strong> one card completes a sequence of five.</li>
                        <li><strong>Stop them:</strong> green is one chip from a sequence. Find the space that blocks it.</li>
                        <li><strong>Win in 2:</strong> make two winning spaces at once, so green can block only one. This is the trick strong players use, and the Hard computer looks for it too.</li>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#3E4A45', lineHeight: 1.8 }}>
                        Solve it on consecutive days to build a streak, see where you rank on today's leaderboard, and share your result with friends. New to the game? Read <Link to="/how-to-play" style={{ color: '#1E6B4F' }}>how to play Sequence</Link> and the <Link to="/strategy" style={{ color: '#1E6B4F' }}>strategy guide</Link>.
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default DailyPuzzle;
